import { CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface TaskDatePickerProps {
  /** Data no formato `YYYY-MM-DD` (ou string vazia quando sem data). */
  value: string;
  onChange: (value: string) => void;
  /** Data mínima selecionável no formato `YYYY-MM-DD`. */
  minDate?: string;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Permite limpar a data selecionada (botão "x"). */
  clearable?: boolean;
}

/** Converte `YYYY-MM-DD` em Date local, evitando o shift de fuso do parser nativo. */
function parseISODate(value: string): Date | undefined {
  if (!value) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

/** Converte Date local em `YYYY-MM-DD` sem usar UTC (que poderia mudar o dia). */
function formatISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Seletor de data compartilhado das tarefas: abre um mini calendário em popover
 * (padrão shadcn) e trabalha com strings `YYYY-MM-DD`, mantendo compatibilidade
 * com o estado existente que substituía o `<input type="date">` nativo.
 */
export function TaskDatePicker({
  value,
  onChange,
  minDate,
  id,
  disabled = false,
  placeholder = "Selecionar data",
  className,
  clearable = false,
}: TaskDatePickerProps) {
  const selected = parseISODate(value);
  const minSelectable = parseISODate(minDate ?? "");

  const showClear = clearable && Boolean(value) && !disabled;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="flex-1 truncate">
            {selected ? format(selected, "dd 'de' MMM 'de' yyyy", { locale: ptBR }) : placeholder}
          </span>
          {showClear && (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Limpar data"
              className="ml-2 inline-flex h-4 w-4 items-center justify-center rounded-sm opacity-60 hover:opacity-100"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange("");
              }}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(date) => onChange(date ? formatISODate(date) : "")}
          locale={ptBR}
          disabled={minSelectable ? (date) => date < minSelectable : undefined}
          className="pointer-events-auto"
        />
      </PopoverContent>
    </Popover>
  );
}
